import Script from 'next/script';
import { FULL_COVERAGE_PIXEL_ID, FULL_FUNNEL_GOAL_SALES_PIXEL_ID } from '@/utils/fbPixel';
import useShouldInjectFbPixel from '@/hooks/useShouldInjectFbPixel';

interface FbPixelScriptProps {
  // 是否额外触发 PageView，默认 true；与原页面 Script 行为一致即可
  enablePageView?: boolean;
  // Script id 后缀，避免单页多次加载冲突
  idSuffix?: string;
  /**
   * 显式指定要 init 的 pixel id 列表（**覆盖**默认的 906 + 946 双 init）。
   *
   * - 未传 / 空数组：走默认 `FB_PIXEL_IDS = [906, 946]`，与历史行为完全一致。
   * - 传一个：单 pixel init（当前唯一用例是 ai-landing-super 走 919）。
   * - 传多个：按数组顺序逐个 init。
   *
   * 设计动机：约课页的 FB pixel 上报有两种归因模式 ——
   *   1. cp-landing 自有英语漏斗（默认）：906 收 Lead-campaign，946 收
   *      Sales-campaign，trackFbBusinessEvent 双发；
   *   2. 上游 ai-landing-super 跳进来（`pageConfig.fromAiLanding === true`
   *      + `pageConfig.fbPixelId` 提供具体 ID）：约课成功只回灌到那一个
   *      pixel（与上游留资阶段同 pixel），避免跨 3 个 pixel 的归因报表
   *      对不齐。
   *
   * `enablePageView` 控制对**全部** init 的 pixel 是否额外触发一次
   * PageView。`appointmentClass` 等明确不想要 PageView 的页面会传 false；
   * 老页面（Lead campaign）维持默认 true。
   */
  pixelIds?: readonly string[];
}

// 全渠道初始化 906（Full coverage event）+ 946（Full funnel events _Goal Sales）
// Purchase w/ Value（1032）不在本次范围，仍由各页面原有的 Script 按渠道条件单独加载
// 微信小程序 / 悟空 app webview 内不加载，避免平台限制与合规问题
//
// 当调用方传入 `pixelIds` 时，逐个 init **覆盖**默认 906+946 这套 ——
// 当前唯一用例：ai-landing-super 跳过来时只 init 919。
const FbPixelScript = ({ enablePageView = true, idSuffix = '', pixelIds }: FbPixelScriptProps) => {
  const shouldInject = useShouldInjectFbPixel();

  if (!shouldInject) return null;

  const scriptId = idSuffix ? `js-facebook-${idSuffix}` : 'js-facebook';

  // pixelIds 为空（含 undefined / 长度 0）时回退到默认 906+946，保持向后兼容。
  // 不直接复用 `FB_PIXEL_IDS` 的引用，而是显式枚举 FULL_COVERAGE/SALES 两个
  // 常量名，方便代码搜索 906/946 init 出现的位置。
  const effectivePixelIds =
    pixelIds && pixelIds.length > 0 ? pixelIds : ([FULL_COVERAGE_PIXEL_ID, FULL_FUNNEL_GOAL_SALES_PIXEL_ID] as const);

  // 使用 trackSingle 精确触发，避免与各页面遗留的 1032 Script 共用全局 fbq('track', 'PageView') 时
  // 因 lazyOnload 时序拼接导致 906/946 收到重复 PageView 或 1032 漏掉 PageView
  const initLines = [
    ...effectivePixelIds.map((id) => `fbq('init', '${id}');`),
    ...(enablePageView ? effectivePixelIds.map((id) => `fbq('trackSingle', '${id}', 'PageView');`) : []),
  ].join('\n            ');

  return (
    <Script
      id={scriptId}
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            ${initLines}
          `,
      }}
    />
  );
};

export default FbPixelScript;
